import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';
import { movimientosApi } from '@/services/movimientosApi';
import { almacenesApi } from '@/services/almacenesApi';
import { formatDate } from '@/utils/formatters';
import { MovimientoForm } from './MovimientoForm';
import { InventoryItemPicker } from './InventoryItemPicker';
import type { InventoryItemRefDto, TipoMovimiento } from '@/types/inventario';

const TIPO_LABELS: Record<TipoMovimiento, string> = {
  ENTRADA: 'Entrada',
  SALIDA: 'Salida',
  TRASLADO: 'Traslado',
  AJUSTE: 'Ajuste',
};

const TIPO_COLORS: Record<TipoMovimiento, string> = {
  ENTRADA: 'bg-emerald-100 text-emerald-700',
  SALIDA: 'bg-red-100 text-red-700',
  TRASLADO: 'bg-blue-100 text-blue-700',
  AJUSTE: 'bg-amber-100 text-amber-700',
};

const TIPOS: TipoMovimiento[] = ['ENTRADA', 'SALIDA', 'TRASLADO', 'AJUSTE'];
const PAGE_SIZE = 20;

export function MovimientosView() {
  const [showForm, setShowForm] = useState(false);
  const [tipo, setTipo] = useState<TipoMovimiento | ''>('');
  const [almacenId, setAlmacenId] = useState<number | ''>('');
  const [item, setItem] = useState<InventoryItemRefDto | null>(null);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [page, setPage] = useState(0);

  const { data: almacenes = [] } = useQuery({
    queryKey: ['almacenes'],
    queryFn: () => almacenesApi.findAll(true),
  });

  const filters = {
    tipo: tipo === '' ? null : tipo,
    almacenId: almacenId === '' ? null : almacenId,
    itemId: item?.id ?? null,
    desde: desde ? new Date(desde).toISOString() : null,
    hasta: hasta ? new Date(hasta + 'T23:59:59').toISOString() : null,
    page,
    size: PAGE_SIZE,
  };

  const { data, isLoading } = useQuery({
    queryKey: ['movimientos', filters],
    queryFn: () => movimientosApi.list(filters),
  });

  const movimientos = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;

  const resetFilters = () => {
    setTipo(''); setAlmacenId(''); setItem(null); setDesde(''); setHasta(''); setPage(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus size={16} /> Nuevo Movimiento
          </button>
          {(tipo || almacenId || item || desde || hasta) && (
            <button
              onClick={resetFilters}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Tipo</label>
          <select
            value={tipo}
            onChange={(e) => { setTipo(e.target.value as TipoMovimiento | ''); setPage(0); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Todos</option>
            {TIPOS.map((t) => <option key={t} value={t}>{TIPO_LABELS[t]}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Almacén</label>
          <select
            value={almacenId}
            onChange={(e) => { setAlmacenId(e.target.value ? Number(e.target.value) : ''); setPage(0); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Todos</option>
            {almacenes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
          </select>
        </div>
        <div className="lg:col-span-1">
          <label className="block text-xs font-medium text-gray-500 mb-1">Ítem</label>
          <InventoryItemPicker value={item} onChange={(it) => { setItem(it); setPage(0); }} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Desde</label>
          <input
            type="date"
            value={desde}
            onChange={(e) => { setDesde(e.target.value); setPage(0); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Hasta</label>
          <input
            type="date"
            value={hasta}
            onChange={(e) => { setHasta(e.target.value); setPage(0); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
          />
        </div>
      </div>

      <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Fecha</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Tipo</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Ítem</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Cant.</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Origen</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Destino</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Referencia</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Usuario</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">Cargando...</td></tr>
            ) : movimientos.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-8 text-gray-500">Sin movimientos</td></tr>
            ) : (
              movimientos.map((m) => (
                <tr key={m.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-sm text-gray-600 whitespace-nowrap">{formatDate(m.fecha)}</td>
                  <td className="px-4 py-2.5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_COLORS[m.tipo]}`}>
                      {TIPO_LABELS[m.tipo]}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-sm">
                    <p className="font-medium text-gray-900 truncate max-w-[200px]">{m.item.nombre}</p>
                    <p className="text-xs text-gray-500 font-mono">{m.item.codigo ?? '—'}</p>
                  </td>
                  <td className="px-4 py-2.5 text-sm font-semibold text-right">{m.cantidad}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{m.almacenOrigen?.nombre ?? '—'}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600">{m.almacenDestino?.nombre ?? '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{m.referencia ?? '—'}</td>
                  <td className="px-4 py-2.5 text-xs text-gray-500">{m.usuarioNombre}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-2">
        {isLoading ? (
          <p className="text-center py-8 text-gray-500">Cargando...</p>
        ) : movimientos.length === 0 ? (
          <p className="text-center py-8 text-gray-500">Sin movimientos</p>
        ) : (
          movimientos.map((m) => (
            <div key={m.id} className="bg-white rounded-xl border border-gray-200 p-3">
              <div className="flex items-center justify-between mb-1">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${TIPO_COLORS[m.tipo]}`}>
                  {TIPO_LABELS[m.tipo]}
                </span>
                <span className="text-sm font-semibold">{m.cantidad} ud</span>
              </div>
              <p className="text-sm font-medium truncate">{m.item.nombre}</p>
              <p className="text-xs text-gray-500">
                {m.almacenOrigen?.nombre ?? '—'} → {m.almacenDestino?.nombre ?? '—'}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">{formatDate(m.fecha)} · {m.usuarioNombre}</p>
              {m.referencia && <p className="text-xs text-gray-500 mt-0.5">Ref: {m.referencia}</p>}
            </div>
          ))
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 px-4 py-2.5">
          <span className="text-xs text-gray-500">Página {page + 1} de {totalPages}</span>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="p-1.5 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={page + 1 >= totalPages}
              className="p-1.5 border border-gray-300 rounded-md disabled:opacity-40 hover:bg-gray-50"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      <MovimientoForm open={showForm} onClose={() => setShowForm(false)} />
    </div>
  );
}
